<template>
  <apexchart
    ref="barChart"
    class="bar-chart"
    :height="height"
    :options="chartOptions"
    :series="series"
    type="bar"
    :width="width"
  />
</template>

<script setup>
import VueApexCharts from 'vue3-apexcharts';

import { ref } from 'vue';
import I18n from 'i18n-js';
import { getLocales } from '@/functions/chart_config';
import { MONTHS } from '@/functions/date_format_helpers';

const apexchart = VueApexCharts;
const barChart = ref('barChart');

const props = defineProps({
  /** Numeric value for the height of a chart */
  height: { type: Number, required: false, default: 100 },
  /** Unique id for the chart. To ensure there is no clashing with multiple charts */
  id: { type: String, required: true, default: '' },
  /** Data for the chart series */
  series: { type: Array, required: true, default: () => [] },
  /** Numeric value for the width of a chart */
  width: { type: Number, required: false, default: 150 },
});

/**
 * Colours for chart options
 */
const colourConfig = {
  barColor: '#29A7F4',
  foreColor: '#989799',
  dataLabels: '#D5D4D6',
};

/**
 * Font sizes for chart options
 */
const fontSizeConfig = {
  dataLabels: '10px',
  tooltipLabels: '13px',
};

/**
 * Chart options
 * (defines what the chart can do and what it looks like)
 */
const chartOptions = {
  chart: {
    id: props.id,
    type: 'bar',
    foreColor: colourConfig.foreColor,
    toolbar: {
      show: false,
    },
    locales: getLocales(I18n),
    defaultLocale: I18n.locale,
  },
  stroke: { width: 1.5 },
  plotOptions: {
    bar: {
      dataLabels: { position: 'top' },
    },
  },
  dataLabels: {
    enabled: true,
    style: {
      fontSize: fontSizeConfig.dataLabels,
      colors: [colourConfig.dataLabels],
    },
    offsetY: -100,
  },
  xaxis: {
    tickPlacement: 'on',
    type: 'datetime',
    position: 'bottom',
    tooltip: {
      enabled: false,
    },
    axisBorder: {
      show: false,
    },
    labels: {
      offsetY: -4,
      style: {
        fontSize: fontSizeConfig.dataLabels,
        fontWeight: 300,
      },
      datetimeFormatter: {
        year: 'yyyy',
        month: 'MMM \'yy',
        day: 'dd',
      },
    },
    lines: {
      show: false,
    },
  },
  yaxis: {
    max: 5,
    labels: {
      show: false,
    },
  },
  grid: {
    show: false,
    padding: {
      left: -15,
      top: -20,
      bottom: -10,
      right: 0,
    },
  },
  colors: [colourConfig.barColor],
  tooltip: {
    enabled: true,
    shared: false,
    followCursor: true,
    fillSeriesColor: false,
    theme: false,
    style: {
      fontSize: fontSizeConfig.tooltipLabels,
    },
    x: {
      show: true,
      formatter: function (value) {
        let date = new Date(value);
        return `${I18n.t(`months.shorthand.${MONTHS[date.getMonth()]}`)} ${date.getFullYear()}`;
      },
    },
  },
};
</script>

<style lang="scss">
@import '@/stylesheets/global';
@import '@/stylesheets/components/apexchart_override.scss';
</style>
